import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import DailyStats from "./TradingStatistics";
import AdminDailyStats from "../admin/AdminDailyStats";
import DailyStatistics from "./DailyStatistics";
import AutoRunBotIcon from "../../AI-COMPONENT/AutoRunBotIcon";
import ConsistencyControl from "../../AI-COMPONENT/ConsistencyControl";

const TradingSettings = ({ amount, trendTracker, onUpdate, isLoading }) => {
  const [formData, setFormData] = useState({
    amount: amount || 0,
    trendTracker: trendTracker || 0,
  });
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Safely extract values from props (in case they're objects with success/data)
  const getSafeValue = (value, fallback = 0, key = null) => {
    // Handle null/undefined
    if (value === undefined || value === null) return fallback;

    // If it's not an object, return as is
    if (typeof value !== 'object') return value;

    // Handle common API response formats
    if ('data' in value) {
      // If data is an object with an amount or value, use that
      if (typeof value.data === 'object' && value.data !== null) {
        if ('amount' in value.data) return value.data.amount;
        if ('value' in value.data) return value.data.value;
        if ('number_of_directions' in value.data) return value.data.number_of_directions;
      }
      return value.data;
    }

    // Handle direct amount/value in object
    if ('amount' in value) return value.amount;
    if ('value' in value) return value.value;
    if ('number_of_directions' in value) return value.number_of_directions;

    // If a specific key is requested, try to get it
    if (key && key in value) return value[key];

    // If it's an array, return the first item or fallback
    if (Array.isArray(value)) return value[0]?.number_of_directions || fallback;

    // Try to get the first value that's not an object
    const firstNonObjectValue = Object.values(value).find(v => typeof v !== 'object');
    if (firstNonObjectValue !== undefined) return firstNonObjectValue;

    // Last resort: return the first value or fallback
    return Object.values(value)[0] || fallback;
  };

  // Update form data when props change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      amount: getSafeValue(amount, 0),
      trendTracker: getSafeValue(trendTracker, 0),
    }));
  }, [amount, trendTracker]);

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;

    // Only update if the value has actually changed
    setFormData(prev => {
      const newValue = value === '' ? '' : Number(value);
      if (prev[name] === newValue) return prev;

      return {
        ...prev,
        [name]: newValue
      };
    });

    if (isSaved) setIsSaved(false);
  };

  const handleSubmit = async (e) => {

    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation?.();
    }

    // Only proceed if not already submitting
    if (isSubmitting) {
      return false;
    }

    setError('');
    setIsSubmitting(true);

    try {
      // Create a copy of form data to work with
      const formDataCopy = { ...formData };

      // Validate form data
      if (formDataCopy.amount === '' || formDataCopy.amount === null || formDataCopy.amount === undefined) {
        throw new Error('Trading amount is required');
      }

      const amountValue = Number(formDataCopy.amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        throw new Error('Trading amount must be greater than 0');
      }

      if (formDataCopy.trendTracker === '' || formDataCopy.trendTracker === null || formDataCopy.trendTracker === undefined) {
        throw new Error('Trend tracker value is required');
      }

      const trendValue = Number(formDataCopy.trendTracker);
      if (isNaN(trendValue) || trendValue < 0 || trendValue > 1000) {
        throw new Error('Trend tracker must be between 0 and 1000');
      }

      // Prepare the data to send
      const updateData = {
        amount: amountValue,
        trendTracker: trendValue
      };

      // Call the parent's update handler and wait for it to complete
      const result = await onUpdate(updateData);

      // If there was an error in the parent handler, throw it
      if (!result || !result.success) {
        throw new Error(result?.message || 'Failed to save settings');
      }

      // Show success message
      setIsSaved(true);

      // Clear success message after delay
      setTimeout(() => {
        setIsSaved(false);
      }, 3000);

      return true;

    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'Failed to save settings');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <DailyStats />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Current Settings Card */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-800 shadow-lg h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-600 rounded-xl shadow-lg">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Current Settings</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Active trading parameters</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trading Amount</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">${getSafeValue(amount, 0)}</p>
                    </div>
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trend Tracker</p>
                      <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">{getSafeValue(trendTracker, 0)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">signals detected</p>
                    </div>
                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Settings Form */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-lg">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Trading Parameters</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Configure your trading settings</p>
                </div>
              </div>

              {error && (
                <div className="rounded-xl bg-red-50 dark:bg-red-900/30 p-4 border-l-4 border-red-500 mb-6 shadow-sm">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              {isSaved && (
                <div className="rounded-xl bg-green-50 dark:bg-green-900/30 p-4 border-l-4 border-green-500 mb-6 shadow-sm">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-semibold text-green-800 dark:text-green-200">Settings saved successfully!</h3>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6 mb-8">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-3">
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Trading Amount (USD)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        name="amount"
                        id="amount"
                        min="0"
                        step="0.01"
                        value={formData.amount}
                        onChange={handleChange}
                        className="h-12 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full pl-8 pr-4 text-base border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 shadow-sm hover:border-blue-400"
                        placeholder="0.00"
                        disabled={isLoading || isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="trendTracker" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Trend Tracker (0-1000 signals)
                    </label>
                    <div className="mt-1">
                      <input
                        type="number"
                        name="trendTracker"
                        id="trendTracker"
                        min="0"
                        max="1000"
                        value={formData.trendTracker}
                        onChange={handleChange}
                        className="h-12 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full px-4 text-base border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 shadow-sm hover:border-indigo-400"
                        placeholder="0"
                        disabled={isLoading || isSubmitting}
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 hover:scale-105 active:scale-95"
                      onClick={() => {
                        setFormData({
                          amount: getSafeValue(amount, 0),
                          trendTracker: getSafeValue(trendTracker, 0)
                        });
                        setError('');
                      }}
                      disabled={isLoading || isSubmitting}
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200 hover:scale-105 active:scale-95"
                      disabled={isLoading || isSubmitting || isSaved}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Automation & Insights Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg shadow-lg">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Automation & Insights</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage AI trading and track performance</p>
            </div>
          </div>
          
          {/* Top Row - Auto Run Bot & Daily Statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Auto Run Bot */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">Auto Run Bot</h4>
              </div>
              <AutoRunBotIcon variant="full" />
            </div>

            {/* Daily Statistics */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <svg className="h-5 w-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">Daily Statistics</h4>
              </div>
              <DailyStatistics />
            </div>
          </div>

          {/* Bottom Row - Consistency Control (Full Width) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
            <div className="flex items-center justify-center">
              <ConsistencyControl />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingSettings;
